'use client'

import { useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { ctaSection } from "@/config/site"
import { ArrowRight, Mail, Phone } from "lucide-react"
import Link from "next/link"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger)
}

export function CtaSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const subtitleRef = useRef<HTMLParagraphElement>(null)
  const buttonsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set([titleRef.current, subtitleRef.current], {
        opacity: 0,
        y: 40,
      })
      gsap.set(buttonsRef.current, {
        opacity: 0,
        y: 24,
      })

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 82%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      })

      tl.to(titleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.75,
        ease: "power3.out",
      }, 0)

      tl.to(subtitleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.55,
        ease: "power2.out",
      }, 0.12)

      tl.to(buttonsRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.65,
        ease: "power3.out",
      }, 0.22)
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={sectionRef}
      className="cta-section"
      aria-labelledby="cta-heading"
    >
      <div className="cta-backdrop--base" aria-hidden />
      <div className="cta-backdrop--radial" aria-hidden />
      <div className="cta-backdrop--blob-left" aria-hidden />
      <div className="cta-backdrop--blob-right" aria-hidden />

      <div className="cta-inner">
        <h2 id="cta-heading" ref={titleRef} className="cta-title">
          {ctaSection.title}
        </h2>
        <p ref={subtitleRef} className="cta-subtitle">
          {ctaSection.subtitle}
        </p>

        <div ref={buttonsRef} className="cta-stack">
          <div className="cta-actions-row">
            <Button asChild className="btn-cta-primary">
              <Link href={ctaSection.primaryCta.href}>
                <Phone aria-hidden />
                {ctaSection.primaryCta.text}
              </Link>
            </Button>
            <Button asChild className="btn-cta-secondary">
              <Link href={ctaSection.secondaryCta.href}>
                {ctaSection.secondaryCta.text}
                <ArrowRight aria-hidden />
              </Link>
            </Button>
          </div>

          <Link href={ctaSection.thirdCta.href} className="link-cta-tertiary">
            <Mail className="link-cta-tertiary-icon" aria-hidden />
            {ctaSection.thirdCta.text}
          </Link>
        </div>
      </div>
    </section>
  )
}
